import { FC, useEffect, useState } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  PhoneInputGroup,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useDispatch } from "react-redux";
import {
  CountryCode,
  countries,
  codeToCountryNameMap,
} from "@funded-here-interface/common/src/constant/country";
import "react-toastify/dist/ReactToastify.css";
import { NO_REGEX } from "@funded-here-interface/common";
import {
  Industry,
  IndustryGroup,
  LegalStructureMerchantsIssuersSuppliers,
} from "../../constant/enum";
import {
  IndustryGroupMap,
  LegalStructuresMerchantAndSPV,
} from "../../constant/dropdownValues";
import { toast } from "react-toastify";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import { useStyles } from "../../styles/BizProfileCss";
import { BizProfileFormData } from "./types";
import { INITIAL_ERRORS, TOAST_MESSAGES } from "./constants";
import {
  hydrateBizProfileToRedux,
  hydrateBizProfileToState,
  formatDateForInput,
  formatPhoneNumber,
  validateBizProfileForm,
} from "./utils/bizProfileUtils";
import { TabComponentProps } from "../SalesOnboarding/types";

const BorrowerBizProfile: FC<TabComponentProps> = ({
  isEditable,
  merchantId,
  onDirtyChange,
  onSaveDraft,
  onResetForm,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const {
    company: comp,
    regNumber: regno,
    legalStructure: legalStructureRedux,
    legalStructureOthers: legalStructureOthersRedux,
    industryGroup: industryGroupRedux,
    industryDropdownValues: industryDropdownValuesRedux,
    industryOthers: industryOthersRedux,
    typeOfInstitute,
    countryOfDomicile: countryOfDomicileRedux,
    countryOfIncorp: countryOfIncorpRedux,
    address: addressRedux,
    postal: postalRedux,
    website: companysiteRedux,
    phoneNumber: phoneNumberRedux,
    incorporationDate: companyStartDateRedux,
  } = useSelector((state: RootState) => state.borBiz);

  const userId = merchantId.toString();
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] =
    useState<BizProfileFormData | null>(null);
  const [initPage, setInitPage] = useState(true);
  const [companyName, setCompany] = useState(comp || "");
  const [registrationNumber, setRegNumber] = useState(regno || "");
  const [legalStructure, setLegStr] = useState(legalStructureRedux || "");
  const [industryGroup, setIndustryGroup] = useState(industryGroupRedux || "");
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >(industryDropdownValuesRedux || []);
  const [industryOthers, setIndustryOthers] = useState(
    industryOthersRedux || ""
  );
  const [institutionType, setInstitute] = useState(typeOfInstitute || "");
  const [countryOfIncorporation, setCountryCountryOfIncorp] = useState(
    countryOfIncorpRedux || ""
  );
  const [countryOfDomicile, setCountryCountryOfDomicile] = useState(
    countryOfDomicileRedux || ""
  );
  const [address, setAddress] = useState(addressRedux || "");
  const [postalcode, setPostalCode] = useState(postalRedux || "");
  const [companyWebsite, setcompanysite] = useState(companysiteRedux || "");
  const [phoneNumber, setPhone] = useState(phoneNumberRedux || "");
  const [companyStartDate, setCompanyStartDate] = useState(
    companyStartDateRedux || ""
  );
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();

  const [error, setError] = useState(INITIAL_ERRORS);

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (!isEditable) {
        setIsDirty(false);
        onDirtyChange(false);
      }
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const data = await fetchDraft(parseInt(userId));
          if (data) {
            const bizProfileData: BizProfileFormData = {
              companyName: data.companyName || "",
              companyRegistrationNo: data.companyRegistrationNo || "",
              companyStartDate: formatDateForInput(data.companyStartDate),
              legalStructure: data.legalStructure || "",
              industryGroup: data.industryGroup || "",
              industry: data.industry || "",
              industryOthers: data.industryOthers || "",
              countryOfIncorporation: data.countryOfIncorporation || "",
              countryOfDomicile: data.countryOfDomicile || "",
              legalEntityAddress: data.legalEntityAddress || "",
              postalCode: data.postalCode?.toString() || "",
              website: data.website || "",
              phoneNumber: data.phoneNumber || "",
              industryDropdownValues:
                IndustryGroupMap[data.industryGroup as IndustryGroup] || [],
            };

            setInitialDraftData(bizProfileData);
            hydrateBizProfileToState(bizProfileData, {
              setCompany,
              setRegNumber,
              setLegStr,
              setIndustryGroup,
              setIndustryDropdownValues,
              setInstitute,
              setIndustryOthers,
              setCountryCountryOfIncorp,
              setCountryCountryOfDomicile,
              setAddress,
              setPostalCode,
              setcompanysite,
              setPhone,
              setCompanyStartDate,
              setCountryCode,
            });
            hydrateBizProfileToRedux(dispatch, bizProfileData);

            setError(INITIAL_ERRORS);
            setIsDirty(false);
            onDirtyChange(false);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId, dispatch]);

  const handleInputChange = (
    setter: Function,
    value: any,
    errorField?: keyof typeof INITIAL_ERRORS
  ) => {
    setter(value);
    if (errorField) {
      setError((prev) => ({ ...prev, [errorField]: "" }));
    }
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange(true);
    }
  };

  const onLegalStructureChange = (
    value: LegalStructureMerchantsIssuersSuppliers
  ) => {
    handleInputChange(setLegStr, value, "legalStructure");
  };

  const onIndustryGroupChange = (value: IndustryGroup) => {
    setIndustryDropdownValues([]);
    setInstitute("");
    let industry: Industry[] = IndustryGroupMap[value] || [];
    handleInputChange(setIndustryGroup, value, "industryGroup");
    handleInputChange(setIndustryDropdownValues, [...industry]);
  };

  const onIndustryChange = (value: Industry) => {
    handleInputChange(setInstitute, value, "institutionType");
  };

  const handleCountryOfDomicileChange = (value: string) => {
    handleInputChange(setCountryCountryOfDomicile, value, "countryOfDomicile");
    const code =
      codeToCountryNameMap[value as keyof typeof codeToCountryNameMap];
    setCountryCode(code as CountryCode);
  };

  const saveDraft = (showToast: boolean = true) => {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);

    const bizProfileData: BizProfileFormData = {
      companyName,
      companyRegistrationNo: registrationNumber,
      legalStructure,
      industryGroup,
      industry: institutionType,
      industryOthers,
      countryOfIncorporation,
      countryOfDomicile,
      legalEntityAddress: address,
      postalCode: postalcode,
      website: companyWebsite,
      phoneNumber: formattedPhoneNumber,
      companyStartDate,
    };

    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        companyName: bizProfileData.companyName,
        companyRegistrationNo: bizProfileData.companyRegistrationNo,
        legalStructure: bizProfileData.legalStructure,
        countryOfIncorporation: bizProfileData.countryOfIncorporation,
        countryOfDomicile: bizProfileData.countryOfDomicile,
        legalEntityAddress: bizProfileData.legalEntityAddress,
        postalCode: bizProfileData.postalCode || undefined,
        website: bizProfileData.website,
        phoneNumber: bizProfileData.phoneNumber,
        industryGroup: bizProfileData.industryGroup,
        industry: bizProfileData.industry,
        companyStartDate: bizProfileData.companyStartDate,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange(false);
          setInitialDraftData(bizProfileData);
          if (showToast) {
            toast.success(TOAST_MESSAGES.DRAFT_SAVED, { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const resetForm = () => {
    if (initialDraftData) {
      hydrateBizProfileToState(initialDraftData, {
        setCompany,
        setRegNumber,
        setLegStr,
        setIndustryGroup,
        setIndustryDropdownValues,
        setInstitute,
        setIndustryOthers,
        setCountryCountryOfIncorp,
        setCountryCountryOfDomicile,
        setAddress,
        setPostalCode,
        setcompanysite,
        setPhone,
        setCompanyStartDate,
        setCountryCode,
      });
      hydrateBizProfileToRedux(dispatch, initialDraftData);

      setError(INITIAL_ERRORS);
      setIsDirty(false);
      onDirtyChange(false);
    }
  };

  useEffect(() => {
    onSaveDraft(saveDraft);
    onResetForm(resetForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  const handleSubmit = () => {
    saveDraft(false);

    const { valid, errors } = validateBizProfileForm({
      companyName,
      registrationNumber,
      companyStartDate,
      legalStructure,
      industryGroup,
      institutionType,
      countryOfIncorporation,
      countryOfDomicile,
      address,
      phoneNumber,
      postalcode,
      companyWebsite,
    });

    if (valid) {
      const bizProfileData: BizProfileFormData = {
        companyName,
        companyRegistrationNo: registrationNumber,
        legalStructure,
        industryGroup,
        industry: institutionType,
        industryOthers,
        countryOfIncorporation,
        countryOfDomicile,
        legalEntityAddress: address,
        postalCode: postalcode,
        website: companyWebsite,
        phoneNumber,
        companyStartDate,
        industryDropdownValues,
      };

      hydrateBizProfileToRedux(dispatch, bizProfileData);
      setIsDirty(false);
      onDirtyChange(false);
      navigate("/bor-rep");
    } else {
      setError(errors);
    }
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <InputGroup
        isErrored={error.companyName}
        onChange={(value: any) => {
          handleInputChange(setCompany, value, "companyName");
        }}
        labelText="Company Name"
        id="companyName"
        value={companyName}
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.registrationNumber}
        onChange={(value: string) => {
          handleInputChange(setRegNumber, value, "registrationNumber");
        }}
        labelText="Registration Number"
        id="registrationNumber"
        value={registrationNumber}
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.companyStartDate}
        onChange={(value: any) =>
          handleInputChange(setCompanyStartDate, value, "companyStartDate")
        }
        labelText="Date of Incorporation"
        value={companyStartDate}
        id="incorporationDate"
        type="date"
      />
      <InputSelect
        isErrored={error.legalStructure}
        onChange={onLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={legalStructure}
        data={LegalStructuresMerchantAndSPV}
        disabled={!isEditable}
      />
      <InputSelect
        isErrored={error.industryGroup}
        onChange={onIndustryGroupChange}
        labelText="Industry Group"
        value={industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
        disabled={!isEditable}
      />
      <InputSelect
        isErrored={error.institutionType}
        onChange={onIndustryChange}
        labelText="Industry"
        value={institutionType}
        id="institutionType"
        data={[...industryDropdownValues].sort((a, b) => a.localeCompare(b))}
        disabled={!isEditable}
      />
      <InputSelect
        isErrored={error.countryOfIncorporation}
        onChange={(value: string) => {
          handleInputChange(
            setCountryCountryOfIncorp,
            value,
            "countryOfIncorporation"
          );
        }}
        labelText="Country of Incorporation"
        id="country"
        value={countryOfIncorporation}
        data={countries}
        disabled={!isEditable}
      />
      <InputSelect
        isErrored={error.countryOfDomicile}
        onChange={handleCountryOfDomicileChange}
        labelText="Country of Domicile"
        id="countryOfDomicile"
        value={countryOfDomicile}
        data={countries}
        disabled={!isEditable}
      />
      <InputGroup
        isErrored={error.address}
        onChange={(value: any) => {
          handleInputChange(setAddress, value, "address");
        }}
        labelText="Address"
        id="address"
        value={address}
        disabled={!isEditable}
      />
      <InputGroup
        isErrored={error.postalcode}
        onChange={(value: any) => {
          handleInputChange(
            setPostalCode,
            value.replace(NO_REGEX, ""),
            "postalcode"
          );
        }}
        labelText="Postal Code"
        id="postalcode"
        value={postalcode}
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.companyWebsite}
        onChange={(value: any) => {
          handleInputChange(setcompanysite, value, "companyWebsite");
        }}
        labelText="Company Website / Online Marketplace Link"
        id="companyWebsite"
        value={companyWebsite}
        readOnly={!isEditable}
      />
      <div style={{ marginBottom: "20px" }}>
        <PhoneInputGroup
          isErrored={error.phoneNumber}
          value={phoneNumber}
          country={countryCode}
          onChange={(value: string) => {
            handleInputChange(setPhone, value || "", "phoneNumber");
          }}
          readOnly={!isEditable}
        />
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
          disabled={!isEditable}
        />
      </div>
    </div>
  );
};

export default BorrowerBizProfile;
